import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { Loader2, Activity } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const SYMPTOM_LABELS = {
  fatigue: "Fatigue", pain: "Douleur", nausea: "Nausées", diarrhea: "Diarrhée", constipation: "Constipation",
  dyspnea: "Dyspnée", appetite: "Appétit", neuropathy: "Neuropathie", mucositis: "Mucite", rash: "Éruption", bleeding: "Saignement", edema: "Œdèmes",
};
const LAB_LABELS = {
  hemoglobin: "Hémoglobine (g/dL)", leukocytes: "Leucocytes (G/L)", neutrophils: "PNN (G/L)", platelets: "Plaquettes (G/L)",
  creatinine: "Créatinine (µmol/L)", clearance: "Clairance (mL/min)", asat: "ASAT (U/L)", alat: "ALAT (U/L)", bilirubin: "Bilirubine (µmol/L)",
  crp: "CRP (mg/L)", sodium: "Sodium (mmol/L)", potassium: "Potassium (mmol/L)", calcium: "Calcium (mmol/L)", albumin: "Albumine (g/L)",
};
const PALETTE = ["#0f766e", "#b45309", "#7c3aed", "#be123c", "#1d4ed8", "#4d7c0f"];

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-800" data-testid={testId}>{value}</p>
  </div>
);

export default function ToxicityByCycleDay() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);
  const [maxDay, setMaxDay] = useState(21);
  const [symptoms, setSymptoms] = useState([]);
  const [marker, setMarker] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/pharma/toxicity-by-cycle-day?months=${months}&max_day=${maxDay}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setSymptoms((prev) => (prev.length ? prev : (res.data.symptom_keys || []).slice(0, 3)));
        setMarker((prev) => prev || (res.data.lab_markers || [])[0] || "");
      })
      .catch(() => toast.error("Erreur chargement toxicité par cure"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [months, maxDay]);

  const rows = useMemo(() => (data?.days || []).map((d) => ({
    day: `J${d.day}`, patients: d.patients, suppressed: d.suppressed,
    grade3plus_pct: d.grade3plus_pct ?? null, mean_global_score: d.mean_global_score ?? null,
    ...Object.fromEntries(symptoms.map((s) => [s, d.symptoms?.[s]?.mean_score ?? null])),
    lab: d.labs?.[marker]?.mean ?? null, lab_g3: d.labs?.[marker]?.grade3plus_pct ?? null,
  })), [data, symptoms, marker]);

  const toggleSymptom = (key) => setSymptoms((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key].slice(-6)));

  if (loading && !data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  if (!data) return null;
  const hasData = data.total_checkins > 0 || data.total_lab_values > 0;

  return (
    <div className="space-y-4" data-testid="toxicity-by-cycle-day">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-600 flex items-center gap-2">Période
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-sm" data-testid="toxicity-months-select">
            {[3, 6, 12, 24].map((m) => <option key={m} value={m}>{m} mois</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-2">Fenêtre après cure
          <select value={maxDay} onChange={(e) => setMaxDay(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-sm" data-testid="toxicity-maxday-select">
            {[14, 21, 28, 42].map((d) => <option key={d} value={d}>J0 → J{d}</option>)}
          </select>
        </label>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Patients avec cure retrouvée" value={data.patients_with_chemo} testId="toxicity-patients" />
        <Stat label="Check-ins rattachés à une cure" value={data.total_checkins} testId="toxicity-checkins" />
        <Stat label="Valeurs bio validées rattachées" value={data.total_lab_values} testId="toxicity-labs" />
        <Stat label="Seuil de masquage" value={`< ${data.min_patients_per_day} patients / jour`} testId="toxicity-threshold" />
      </div>

      {!hasData && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-sm text-slate-500 text-center" data-testid="toxicity-empty">
          Aucun check-in ni valeur biologique validée rattachable à une séance HDJ réalisée sur la période. Les séances arrivent via l'import du planning secrétariat ou la validation HDJ.
        </div>
      )}

      {hasData && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="toxicity-symptom-chart">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-500" /> Score moyen des symptômes et % de check-ins avec grade CTCAE ≥ 3</h3>
              <div className="flex flex-wrap gap-1">
                {(data.symptom_keys || []).map((k) => (
                  <button key={k} onClick={() => toggleSymptom(k)} data-testid={`toxicity-symptom-${k}`}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${symptoms.includes(k) ? "bg-slate-800 text-white border-slate-800" : "text-slate-600 border-slate-300 hover:bg-slate-100"}`}>
                    {SYMPTOM_LABELS[k] || k}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={rows} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="score" domain={[0, 3]} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v, name) => [v == null ? "—" : v, name]} labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.patients ?? 0} patients${p?.[0]?.payload?.suppressed ? " (masqué)" : ""}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine yAxisId="score" x="J0" stroke="#94a3b8" strokeDasharray="4 4" label={{ value: "Cure", fontSize: 10, fill: "#64748b", position: "insideTopRight" }} />
                {symptoms.map((s, i) => (
                  <Line key={s} yAxisId="score" type="monotone" dataKey={s} name={SYMPTOM_LABELS[s] || s} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                ))}
                <Line yAxisId="pct" type="monotone" dataKey="grade3plus_pct" name="% grade ≥ 3" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 3" connectNulls dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {(data.lab_markers || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="toxicity-lab-chart">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Biologie validée : moyenne et % grade ≥ 3 par jour depuis la cure</h3>
                <select value={marker} onChange={(e) => setMarker(e.target.value)} className="border rounded-lg px-2 py-1 text-sm" data-testid="toxicity-marker-select">
                  {data.lab_markers.map((m) => <option key={m} value={m}>{LAB_LABELS[m] || m}</option>)}
                </select>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={rows} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="val" tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip formatter={(v, name) => [v == null ? "—" : v, name]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="val" type="monotone" dataKey="lab" name={LAB_LABELS[marker] || marker} stroke="#0f766e" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                  <Line yAxisId="pct" type="monotone" dataKey="lab_g3" name="% grade ≥ 3" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 3" connectNulls dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
      <p className="text-[11px] text-slate-400" data-testid="toxicity-methodology">{data.methodology}</p>
    </div>
  );
}
